/**
 * Module dependencies.
 */

import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'app/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { Title } from 'app/components/atoms/title/title';
import { Field } from 'app/components/organisms/field/field';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatNumber } from '../transactions/form-config';
import { SubTitle } from 'app/components/atoms/sub-title/sub-title';
import { cn } from 'app/lib/utils';
import { Button } from 'app/components/atoms/button/button';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import { usePostEditTransaction } from 'app/hooks/requests/apps-crypto/use-post-edit-transaction';

/**
 * `Props` type.
 */

type Props = {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  refetch: () => any;
};

/**
 * Export `Label` component.
 */

export function Label({ className, ...rest }: React.HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return <span className={cn('whitespace-nowrap', className)} {...rest} />;
}

function OptionItem({ transaction, first }: any) {
  return (
    <table className={cn('w-full rounded-2xl', !first && '-mt-[6px]')}>
      {!!first && (
        <thead>
          <tr>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 50 }}>
              {'ID'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 100 }}>
              {'Date'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 150 }}>
              {'Amount'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 150 }}>
              {'Amount Rest'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 100 }}>
              {'Profit Value'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 150 }}>
              {'Profit Percent'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 150 }}>
              {'Unit Price'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 100 }}>
              {'Type'}
            </th>
            <th className={'bg-[#999] p-0 text-left text-white'} style={{ width: 100 }}>
              {'Airdrop'}
            </th>
          </tr>
        </thead>
      )}

      {transaction && (
        <tbody>
          <tr key={transaction.id}>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 50 }}>
              {transaction.id}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 100 }}>
              {moment(transaction.createdAt).format('DD.MM.YYYY')}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 150 }}>
              {transaction.amount}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 150 }}>
              {transaction.amountRest}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 100 }}>
              {formatNumber(transaction.currentProfit, '$')}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 150 }}>
              {transaction.currentProfitPercentage}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 150 }}>
              {formatNumber(transaction.unitPrice, '$')}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 100 }}>
              {transaction.type}
            </td>
            <td className={'border-b border-[#ddd] p-0'} style={{ width: 100 }}>
              {transaction.isAirdrop === 1 ? 'YES' : 'NO'}
            </td>
          </tr>
        </tbody>
      )}
    </table>
  );
}

/**
 * Export `FixAmountsModal` component.
 */

export function FixAmountsModal({ isOpen, onClose, data }: Props): JSX.Element {
  const [selectedItems, setSelectedItems] = useState<any>({});
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedItems({});
    }
  }, [isOpen]);

  const { coins, totalUsd } = useMemo(() => {
    if (!data) {
      return {
        totalUsd: new BigNumber('0'),
        coins: []
      };
    }

    const coins = data.coins.filter((item: any) => {
      return item.sync?.needSync;
    });

    let totalUsd = new BigNumber('0');

    for (const coin of coins) {
      totalUsd = totalUsd.plus(coin.sync.diffUsd);

      coin.options = [];
      let selected = selectedItems[coin.coin];
      let currentValue = null;

      for (const transaction of coin.transactions) {
        if (transaction.type === 'finished') {
          continue;
        }

        coin.options.push({
          transaction,
          value: transaction.id
        });

        if (
          !selectedItems[coin.coin] &&
          (selected === undefined ||
            currentValue === null ||
            new BigNumber(currentValue).isGreaterThan(transaction.currentProfitPercentage))
        ) {
          selected = transaction.id;
          currentValue = transaction.currentProfitPercentage;
        }
      }

      coin.currentOption = selected;
    }

    return { coins, totalUsd };
  }, [data, selectedItems]);

  const { mutate: updateTransaction, ...rest } = usePostEditTransaction();

  console.log(rest);

  const handleChange = useCallback(async () => {
    setLoading(true);

    for (const coin of coins) {
      const selectedTransactionId = selectedItems[coin.coin] ?? coin.currentOption;
      const selectedTransaction = coin.transactions.find(
        (item: any) => String(item.id) === String(selectedTransactionId)
      );
      const amount = new BigNumber(selectedTransaction.amount).plus(coin.sync.diff).toFixed(8).toString();
      const total = new BigNumber(selectedTransaction.total);
      const unitPrice = total.dividedBy(amount).toFixed(8).toString();

      await updateTransaction({
        transactionId: selectedTransaction.id,
        amount: amount.toString(),
        total: total.toString(),
        unitPrice: unitPrice.toString()
      });
    }
  }, [coins, selectedItems, updateTransaction]);

  useEffect(() => {
    if (coins.length === 0 || !isOpen) {
      onClose();
      setLoading(false);
    }
  }, [coins.length, isOpen, onClose]);

  return (
    <Dialog
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}
      open={isOpen}
    >
      <DialogContent className={'max-h-[85vh] w-full max-w-[1800px] overflow-y-auto'}>
        <DialogTitle className={'sr-only'}>{'Fix all amounts'}</DialogTitle>

        <Title>{'Fix all amounts'}</Title>

        <Field title={'Current Distribution'} value={formatNumber(totalUsd?.toFixed(), '$') as string} />

        <Field title={'Total Coins'} value={coins.length} />

        <SubTitle className={'mt-8 text-[#333]'}>{'Fix with transaction:'}</SubTitle>

        <table className={'w-full'}>
          <thead>
            <tr>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}></th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Coin'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Amount'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Amount USD'}</th>
              <th className={'bg-[#999] px-2 py-1 text-left text-white'}>{'Transaction'}</th>
            </tr>
          </thead>

          <tbody>
            {coins.map((coin: any) => (
              <tr key={coin.coin}>
                <td className={'border-b border-[#ddd] p-2'}></td>
                <td className={'border-b border-[#ddd] p-2'}>
                  <Label>{coin.coin}</Label>
                </td>
                <td className={'border-b border-[#ddd] p-2'}>
                  <Label>{formatNumber(coin.sync.diff)}</Label>
                </td>
                <td className={'border-b border-[#ddd] p-2'}>
                  <Label>{formatNumber(coin.sync.diffUsd, '$')}</Label>
                </td>
                <td className={'w-full border-b border-[#ddd] p-2'}>
                  <Select
                    onValueChange={value => {
                      setSelectedItems((data: any) => ({
                        ...data,
                        [coin.coin]: value
                      }));
                    }}
                    value={coin.currentOption != null ? String(coin.currentOption) : undefined}
                  >
                    <SelectTrigger className={'w-full'}>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      {coin.options.map((option: any, index: number) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          <OptionItem first={index === 0} transaction={option.transaction} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <DialogFooter>
          <div className={'flex justify-end gap-2'} key={'actions'}>
            <Button onClick={onClose}>{'Cancel'}</Button>

            <Button isLoading={isLoading} onClick={handleChange} type={'primary'}>
              {'OK'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
